import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { PageResponse } from '../models/models';
import { ProjectDoc, ProjectDocReferential } from '../models/models';

@Injectable({
  providedIn: 'root'
})
export class ProjectDocService {
  private http = inject(HttpClient);
  private readonly apiUrl = `${environment.apiUrl}/projects`;

  getDocReferentials(search = '', page = 0, size = 10, sort = 'name,asc', showArchived = false): Observable<PageResponse<ProjectDocReferential>> {
    const params = new HttpParams()
      .set('search', search)
      .set('page', String(page))
      .set('size', String(size))
      .set('sort', sort)
      .set('showArchived', String(showArchived));
    return this.http.get<PageResponse<ProjectDocReferential>>(`${this.apiUrl}/doc-referentials`, { params });
  }

  createDocReferential(referential: ProjectDocReferential): Observable<ProjectDocReferential> {
    return this.http.post<ProjectDocReferential>(`${this.apiUrl}/doc-referentials`, referential);
  }

  updateDocReferential(id: number, referential: ProjectDocReferential): Observable<ProjectDocReferential> {
    return this.http.put<ProjectDocReferential>(`${this.apiUrl}/doc-referentials/${id}`, referential);
  }

  deleteDocReferential(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/doc-referentials/${id}`);
  }

  canDeleteDocReferential(id: number): Observable<boolean> {
    return this.http.get<boolean>(`${this.apiUrl}/doc-referentials/${id}/can-delete`);
  }

  getDocsByProject(projectId: number): Observable<ProjectDoc[]> {
    return this.http.get<ProjectDoc[]>(`${this.apiUrl}/${projectId}/docs`);
  }

  uploadDoc(projectId: number, referentialId: number, file: File, comment?: string, docReference?: string): Observable<ProjectDoc> {
    const formData = new FormData();
    formData.append('file', file);
    if (comment) formData.append('comment', comment);
    if (docReference) formData.append('docReference', docReference);
    return this.http.post<ProjectDoc>(`${this.apiUrl}/${projectId}/docs/${referentialId}`, formData);
  }

  updateDocComment(docId: number, comment: string): Observable<ProjectDoc> {
    return this.http.put<ProjectDoc>(
      `${this.apiUrl}/docs/${docId}/comment`,
      comment,
      { headers: { 'Content-Type': 'text/plain' } }
    );
  }

  updateDocReference(docId: number, docReference: string): Observable<ProjectDoc> {
    return this.http.put<ProjectDoc>(
      `${this.apiUrl}/docs/${docId}/reference`,
      docReference,
      { headers: { 'Content-Type': 'text/plain' } }
    );
  }

  deleteDoc(docId: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/docs/${docId}`);
  }

  downloadDoc(docId: number): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/docs/${docId}/download`, {
      responseType: 'blob'
    });
  }

  previewDoc(docId: number): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/docs/${docId}/preview`, {
      responseType: 'blob'
    });
  }
}
